import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SessionActions } from '../../modules/session/session-controller/actions';
import { SessionSelectors } from '../../modules/session/session-controller/selectors';
import { SessionStates } from '../../modules/session/session-controller/states';
import { DropDownService } from '../../services/drop-down-service';
import { ReplaySubject, Subject, combineLatest } from 'rxjs';
import { ApiVisa } from 'blg-akaun-ts-lib';
import { Store } from '@ngrx/store';
import { take, takeUntil } from 'rxjs/operators';
import { ClientSidePermissionStates } from '../../modules/permission/client-side-permissions-controller/states';
import { ClientSidePermissionsSelectors } from '../../modules/permission/client-side-permissions-controller/selectors';
import { SubSink } from 'subsink2';
import { SharedUtilitiesHelperFunctions } from '../helper-functions';

@Component({
  selector: 'app-select-multi-employee-drop-down',
  templateUrl: './select-multi-employee-drop-down.component.html',
  styleUrls: ['./select-multi-employee-drop-down.component.css']
})
export class SelectMultiEmployeeDropDownComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  @Input() employee: FormControl;
  @Input() apiVisa: ApiVisa;
  @Input() selectedGuid: string;
  @Input() label = 'Employee';
  @Input() options: any;

  @Output() employeeSelected = new EventEmitter<any>();

  serverSideFiltering = new FormControl();
  filteredOptions$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  selectedRow: any;
  rowData: any = [];
  key = "employee";
  filteredCache: any = [];
  /** flags to set the toggle all checkbox state */
  isIndeterminate = false;
  isChecked = false;
  protected _onDestroy = new Subject<void>();
  clientSidePermissions$ = this.permissionStore.select(
    ClientSidePermissionsSelectors.selectAll
  );
  POS_VIEW_CASHBILL_ALL_SALES_AGENT = false;
  POS_VIEW_ALL_SALES_AGENT_FROM_PERM = false;
  DEFAULT_SALESAGENT;
  constructor(private dropDownService: DropDownService,
    private readonly store: Store<SessionStates>,
    protected readonly permissionStore: Store<ClientSidePermissionStates>
  ) {

  }

  ngOnInit() {
    this.subs.sink = combineLatest(
      [
        this.clientSidePermissions$,
        this.store.select(SessionSelectors.selectPersonalSettings),
        this.store.select(SessionSelectors.selectMasterSettings),
      ]).subscribe({
        next: ([clientPerm, personal, master]: any) => {
          this.POS_VIEW_ALL_SALES_AGENT_FROM_PERM =  master?.POS_VIEW_ALL_SALES_AGENT_FROM_PERM;
          this.DEFAULT_SALESAGENT = personal?.DEFAULT_SALESAGENT;
          clientPerm.forEach(permission => {
            if (permission.perm_code === "POS_VIEW_CASHBILL_ALL_SALES_AGENT") {
              this.POS_VIEW_CASHBILL_ALL_SALES_AGENT = true;
            }
          })
      }})

    this.store.select(SessionSelectors.selectDropDownRowData).subscribe(resolved => {
      this.rowData = resolved?resolved[this.key]?resolved[this.key]:[]:[];
      if (this.rowData.length === 0) {
        this.getRowData();
      }
      else {
        this.filteredOptions$.next(this.rowData.slice());
      }
    })

    // listen for search field value changes
    this.serverSideFiltering.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filtering();
      });
  }

  ngOnChanges() {
    // console.log('ngOnChanges');
  }

  getRowData(keyword?) {
    if (!this.key) return;

    let formData = {
      "limit" : 50,
      "orderBy" : "name",
      "order" : "ASC",
      "keyword" : ( keyword ? keyword : '')
    }
    if(this.POS_VIEW_ALL_SALES_AGENT_FROM_PERM && !this.POS_VIEW_CASHBILL_ALL_SALES_AGENT && this.DEFAULT_SALESAGENT){
      Object.assign(formData,
        {
          "filterConditions": [
          {
            "filterColumn": "guid",
            "filterValues": [this.DEFAULT_SALESAGENT],
            "filterOperator": "IN"
          }]
        }
      )
    }
    this.dropDownService.get(this.key, formData, this.apiVisa).subscribe(resolved => {
      this.rowData = [...this.rowData, ...resolved.data];
      this.rowData = SharedUtilitiesHelperFunctions.removeDuplicate(this.rowData, 'guid');

      // sorting
      const sorted = this.rowData.sort((a, b) => (a.name < b.name ? -1 : 1));

      this.store.dispatch(SessionActions.selectDropDownRowData({key: this.key, rowData: this.rowData}));

      //fill the drop down
      if (keyword) {
        this.filteredOptions$.next(resolved.data.slice());
      }
      else {
        this.filteredOptions$.next(this.rowData.slice());
      }
    });
  }

  filtering() {
    if (!this.rowData) {
      return;
    }

    // get the search keyword
    let search = this.serverSideFiltering.value;
    if (!search || search.length < 3) {
      this.filteredOptions$.next(this.rowData.slice());
      return;
    } else {
      search = search.toLowerCase();
      this.getRowData(search);
    }
  }

  onSelect(guid: string) {
    if (guid) {
      this.employee.patchValue(guid);
      this.selectedRow = this.rowData.find(row => row.guid === guid);
      this.employeeSelected.emit(this.selectedRow);
    }
  }

  isRequired() {
    let value = this.options['required'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  isMultiple() {
    let value = this.options['multiple'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  isShowAll() {
    let value = this.options['showAll'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  toggleSelectAll(selectAllValue: boolean) {
    this.filteredOptions$.pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(val => {
        if (selectAllValue) {
          this.employee.patchValue([...val.map(item => item.guid)]);
        } else {
          this.employee.patchValue([]);
        }
      });
  }

  setToggleAllCheckboxState() {
    if (!this.isMultiple()) return;
    let filteredLength = 0;
    if (this.employee && this.employee.value) {
      this.filteredCache.forEach(el => {
        if (this.employee.value.indexOf(el) > -1) {
          filteredLength++;
        }
      });
      this.isIndeterminate = filteredLength > 0 && filteredLength < this.filteredCache.length;
      this.isChecked = filteredLength > 0 && filteredLength === this.filteredCache.length;
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

}
